import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { CurrencyCode, formatAmount } from '@percent/utility'
import { getDate } from '@percent/admin-dashboard/common/utility'
import type { BadgeVariant, IconRegistry } from '@percent/lemonade'
import { ProgramCandidate } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'

export function parseConditionValue(val: string): string | number | boolean {
  if (val === 'true') return true

  if (val === 'false') return false
  const num = Number(val)

  if (!Number.isNaN(num) && val.trim() !== '') return num

  return val
}

export const useGrantsUtils = () => {
  const { t } = useTranslation()

  const toIsoOrNull = (value?: Date | null) => (value instanceof Date ? value.toISOString() : value)

  const toNumberOrNull = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') return null
    const num = Number(value)

    return Number.isNaN(num) ? null : num
  }

  const toNullIfEmpty = (value?: string | null) => (value === '' ? null : value)

  const formatCurrency = (amount: number | null | undefined, currencyCode?: CurrencyCode) => {
    if (!amount) return t('status.na')

    return formatAmount({ value: amount, currencyCode: currencyCode ?? 'USD' })
  }

  const formatDate = (date?: string | null) => {
    if (!date) return t('status.na')

    return getDate(date)
  }

  const getStateBadge = (
    candidate: ProgramCandidate,
    translateFn: TFunction
  ): {
    label: string
    variant: BadgeVariant
    icon?: keyof typeof IconRegistry
    tooltip?: string
  } => {
    if (candidate.deletedAt) {
      return {
        label: translateFn('grants.candidateState.deleted', {
          defaultValue: 'Deleted'
        }),
        variant: 'critical',
        icon: 'bin'
      }
    }

    switch (candidate.currentState) {
      case 'Verified':
        return {
          label: translateFn('grants.candidateState.verified', {
            defaultValue: 'Verified'
          }),
          variant: 'default'
        }
      case 'Finalists':
        return {
          label: translateFn('grants.candidateState.finalists', {
            defaultValue: 'Finalists'
          }),
          variant: 'default'
        }
      case 'Rejected':
        return {
          label: translateFn('grants.candidateState.rejected', {
            defaultValue: 'Rejected'
          }),
          variant: 'critical',
          icon: 'reject',
          tooltip: candidate.rejectionReason ?? undefined
        }
      case 'Donation Paid':
        return {
          label: translateFn('grants.candidateState.donationPaid', {
            defaultValue: 'Donation Paid'
          }),
          variant: 'positive'
        }
      default:
        return { label: candidate.currentState, variant: 'default' }
    }
  }

  const parseDate = (value?: string): Date | null => {
    if (!value) return null
    const date = new Date(value)

    return Number.isNaN(date.getTime()) ? null : date
  }

  const toAlphabeticalCompare = (a: string, b: string) => a.localeCompare(b)

  return {
    parseDate,
    toIsoOrNull,
    toNumberOrNull,
    toNullIfEmpty,
    formatCurrency,
    formatDate,
    getStateBadge,
    toAlphabeticalCompare
  }
}
